import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useFetch, formatDate } from "@/lib/content";
import { useSEO, articleSchema } from "@/lib/seo";
import { Reveal, ParallaxImage } from "@/components/site/Motion";
import RichText from "@/components/site/RichText";

export default function PostDetail() {
  const { slug } = useParams();
  const { data: p, loading, error } = useFetch(`/posts/${slug}`);

  useSEO({
    title: p?.seo_title || (p ? `${p.title} | RenB Insights` : "Post | RenB Insights"),
    description: p?.seo_description || p?.excerpt,
    type: "article",
    path: `/insights/${slug}`,
    image: p?.cover_image,
    jsonLd: p ? [articleSchema(p)] : [],
  });

  if (loading)
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-20">
        <p className="text-xs uppercase tracking-[0.25em] text-slategray">Loading…</p>
      </div>
    );

  if (error || !p)
    return (
      <div className="mx-auto max-w-[1440px] px-5 pb-24 pt-40 md:px-10">
        <h1 className="font-serif text-5xl">Post not found.</h1>
        <Link to="/insights" className="mt-6 inline-block text-sm font-bold uppercase tracking-[0.14em] text-progress">
          ← All insights
        </Link>
      </div>
    );

  return (
    <article data-testid="post-detail" className="mx-auto max-w-[1440px] px-5 pb-24 pt-28 md:px-10 md:pt-40">
      <header className="max-w-4xl">
        <Link to="/insights" data-testid="post-back-link" className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slategray transition-colors hover:text-progress">
          <ArrowLeft size={14} /> Insights
        </Link>
        <Reveal className="mt-8">
          <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-progress">
            {formatDate(p.published_at)} · {p.author}
            {p.tags ? ` · ${p.tags}` : ""}
          </p>
          <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tight md:text-6xl">{p.title}</h1>
          {p.excerpt && <p className="mt-6 font-serif text-2xl italic leading-snug text-obsidian/60">{p.excerpt}</p>}
        </Reveal>
      </header>

      {p.cover_image && (
        <ParallaxImage src={p.cover_image} alt={p.title} className="mt-14 aspect-[21/9] border-y border-ash" />
      )}

      <Reveal className="mx-auto mt-14 max-w-3xl">
        <RichText html={p.body} />
      </Reveal>

      <div className="mx-auto mt-20 max-w-3xl border-t border-ash pt-10">
        <p className="font-serif text-3xl font-medium tracking-tight">Where process meets progress.</p>
        <Link to="/book" data-testid="post-cta" className="mt-4 inline-block text-sm font-bold uppercase tracking-[0.14em] text-progress underline-offset-4 hover:underline">
          Book a Discovery Call →
        </Link>
      </div>
    </article>
  );
}
